import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import logo from '../../assets/img/56.png';

const ResetPasswordPage = ({ setCurrentPage }) => {
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Obtener token y email de los parámetros de la URL
    const [token, setToken] = useState('');
    const [email, setEmail] = useState('');

    useEffect(() => {
        // Parsear query params tanto de la URL principal como del hash (#/reset-password?...)
        const hash = window.location.hash;
        const searchParams = new URLSearchParams(window.location.search || (hash.includes('?') ? hash.split('?')[1] : ''));
        
        const urlToken = searchParams.get('token');
        const urlEmail = searchParams.get('email');

        if (urlToken) setToken(urlToken);
        if (urlEmail) setEmail(urlEmail);

        if (!urlToken || !urlEmail) {
            setError('Enlace de recuperación inválido o incompleto');
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!newPassword || !confirmPassword) {
            setError('Por favor completa todos los campos');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Las contraseñas no coinciden');
            return;
        }

        // Validación de contraseña fuerte
        const passwordErrors = [];
        if (newPassword.length < 8) passwordErrors.push('mínimo 8 caracteres');
        if (!/[A-Z]/.test(newPassword)) passwordErrors.push('1 mayúscula');
        if (!/[a-z]/.test(newPassword)) passwordErrors.push('1 minúscula');
        if (!/[0-9]/.test(newPassword)) passwordErrors.push('1 número');
        if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/~`]/.test(newPassword)) passwordErrors.push('1 símbolo');

        if (passwordErrors.length > 0) {
            setError(`La contraseña debe tener: ${passwordErrors.join(', ')}`);
            return;
        }

        if (!token || !email) {
            setError('Falta información de recuperación (token o email)');
            return;
        }

        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    email: email.trim().toLowerCase(), 
                    token: token.trim(), 
                    newPassword 
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Error al restablecer la contraseña');
            }

            setSuccess(true);
            // Limpiar URL para no dejar el token visible
            window.history.replaceState({}, document.title, window.location.pathname);
        } catch (err) {
            setError(err.message || 'Ocurrió un error inesperado');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white flex flex-col">
            {/* Header azul oscuro */}
            <div className="bg-[#1e3a8a] h-3 w-full"></div>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center p-6">
                <div className="bg-white rounded-lg shadow-lg p-10 w-full max-w-md border border-gray-100">
                    {/* Logo y Título estilo LoginPage */}
                    <div className="text-center mb-8">
                        <div className="flex justify-center items-center gap-4 mb-2">
                            <img
                                src={logo}
                                alt="EBID Logo"
                                className="w-20 h-20 object-contain"
                            />
                            <div className="text-left">
                                <h1 className="text-lg font-bold text-[#2c3e7d]">Sistema Académico</h1>
                                <p className="text-[#2c3e7d] text-xs">Escuela Boliviana</p>
                                <p className="text-[#2c3e7d] text-xs">Intercultural de Danza</p>
                            </div>
                        </div>
                    </div>

                    <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Restablecer Contraseña</h2>

                    {!success ? (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {error && (
                                <div className="bg-red-50 border-l-4 border-red-500 p-4 text-red-700 text-sm">
                                    {error}
                                </div>
                            )}

                            {/* Nueva Contraseña */}
                            <div>
                                <label className="block text-gray-700 text-sm mb-2">
                                    Nueva contraseña
                                </label>
                                <div className="relative">
                                    <input
                                        type={showNewPassword ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-full focus:outline-none focus:border-[#1e3a5f] transition-colors pr-12"
                                        placeholder="Ingresa tu nueva contraseña"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1e3a5f] hover:text-[#2c5282] transition-colors"
                                    >
                                        {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    8+ caracteres, mayúscula, minúscula, número, símbolo
                                </p>
                            </div>

                            {/* Confirmar Nueva Contraseña */}
                            <div>
                                <label className="block text-gray-700 text-sm mb-2">
                                    Confirmar nueva contraseña
                                </label>
                                <div className="relative">
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-full focus:outline-none focus:border-[#1e3a5f] transition-colors pr-12"
                                        placeholder="Repite tu contraseña"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1e3a5f] hover:text-[#2c5282] transition-colors"
                                    >
                                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={loading}
                                className={`w-full bg-[#1e3a5f] text-white font-semibold py-3 rounded-full transition-colors shadow-md 
                  ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#2c5282]'}`}
                            >
                                {loading ? 'Guardando...' : 'Guardar Nueva Contraseña'}
                            </button>

                            <div className="text-center mt-4">
                                <button
                                    type="button"
                                    onClick={() => setCurrentPage('login')}
                                    className="text-gray-500 hover:text-[#1e3a5f] text-sm hover:underline"
                                >
                                    Cancelar y volver al inicio
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="text-center space-y-6">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
                                <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900">¡Contraseña restablecida!</h3>
                            <p className="text-gray-500">
                                Tu contraseña ha sido actualizada correctamente. Ya puedes iniciar sesión con tus nuevas credenciales.
                            </p>
                            <button
                                type="button"
                                onClick={() => setCurrentPage('login')}
                                className="w-full bg-[#1e3a5f] hover:bg-[#2c5282] text-white font-semibold py-3 rounded-full transition-colors shadow-md"
                            >
                                Ir al Inicio de Sesión
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
